import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, firstValueFrom, takeUntil, tap, catchError, take, timeout } from 'rxjs';
import * as fromAuth from '../modules/core/store/reducers/auth.reducers';
import * as fromApp from '../modules/core/store/app.state';
import * as AuthActions from '../modules/core/store/actions/auth.actions';
import { Store } from '@ngrx/store';
import { AuthService } from './auth.service';
import { environment as env } from '../../environments/environment';
import { SocketJobsNotificationDataType } from '@app/modules/shared/interfaces/socket-jobs-notification-data.type';
import { JobActions, JobStatusType } from '@app/modules/shared/interfaces/background-notification-data.type';
import { LoggingService, LogLevel } from './logging.service';
import { FingerprintService } from '@app/services/fingerprint.service';
import { PrefixTrie } from '@app/modules/shared/classes/prefix-trie';
import { RafWatchdogService } from './raf-watchdog.service';
import { PayloadValidatorService } from './payload-validator.service';
import { CircuitBreakerService } from './circuit-breaker.service';
import { MessageQueueService, QueueMessage } from './message-queue.service';
import { SocketWorkerTimerService } from './socket-worker-timer.service';
import { WebLockService } from './web-lock.service';
import { SocketConnectionState } from '@app/modules/shared/interfaces/app-analytics-state.type';

/**
 * Decode JWT token without external library
 * Returns the payload or null if invalid
 */
function decodeJwtPayload(token: string): { exp?: number; iat?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    // Base64Url decode
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if JWT token is expired or about to expire
 * @param token JWT token string
 * @param bufferSeconds Consider token expired if it will expire within this many seconds (default: 60)
 * @returns true if token is expired or will expire soon
 */
function isTokenExpired(token: string | null, bufferSeconds: number = 60): boolean {
  if (!token) return true;
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp < (nowSeconds + bufferSeconds);
}

// --- STATE MACHINE ---
// Defines all possible states of the WebSocket connection lifecycle.
type ConnectionState =
  | 'IDLE'                // Initial state, waiting for init()
  | 'WAITING_FOR_AUTH'    // Initialized, but user is not authenticated
  | 'CONNECTING'          // WebSocket connection attempt in progress
  | 'CONNECTED'           // Active WebSocket connection
  | 'RECONNECTING'        // Connection lost, waiting with backoff to reconnect
  | 'DISCONNECTED';       // Intentionally disconnected (e.g., logout)

// Defines all events that can trigger a state transition.
type ConnectionEvent =
  | { type: 'INITIALIZE' }
  | { type: 'AUTH_STATUS_CHANGED'; isAuthenticated: boolean; token: string | null }
  | { type: 'MANUAL_CONNECT' }
  | { type: 'CONNECTION_SUCCESSFUL' }
  | { type: 'CONNECTION_FAILED'; error?: any }
  | { type: 'CONNECTION_LOST'; code: number; reason: string; wasClean: boolean }
  | { type: 'LOGOUT' }
  | { type: 'RECONNECT_TIMER_ELAPSED' };


export interface ChannelStream {
  channelName: string;
  responseCount: number;
  closeAfterFirstResponse: boolean;
  data$: Subject<any>;
}

type Health = 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY';

interface PersistentChannelInfo {
  subject: Subject<any>;
  closeAfterFirstResponse: boolean;
  isActive: boolean;
  lastMessage?: any;
}

interface RetryRecord {
  timeout: any;
  payload: any;
  retriesLeft: number;
  id: string;
}

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
  static readonly COMPONENT_NAME = 'SocketService';

  // --- Core state ---
  private initDone = false; // Remains to prevent premature actions before init() is called
  private firstAuthCheck = true;
  private browserId: string | null = null;
  private endPoint = '';
  private socket: WebSocket | null = null;
  public socketConnectionStatus$ = new BehaviorSubject<boolean>(false);

  // --- State Machine ---
  private connectionState: ConnectionState = 'IDLE';

  // --- Channels / subscriptions ---
  private channels = new Map<string, ChannelStream>();
  private prefixTrie = new PrefixTrie();
  private activePrefixes = new Set<string>();
  private activeChannelSubscriptions = new Map<string, PersistentChannelInfo>();
  private serverSubscribed = new Set<string>(); // de-dupe subscribe/unsubscribe per connection

  // --- Config ---
  private readonly cfg = {
    maxRetriesInConnCheckInterval: 3,
    connCheckIntervalMs: 60_000,
    frameTimeoutMs: 3000,
    reconnect: { baseDelay: 1000, maxDelay: 30000 },
    messageBatch: { maxBatchSize: 20, batchDelayMs: 50, maxQueueSize: 100, policy: 'drop-oldest' as const, ttlMs: 30000 },
    retry: { baseDelay: 250, maxRetries: 5, backoffMultiplier: 1.5, jitterFactor: 0.2, maxRetryQueueSize: 10 },
    circuitBreaker: { failureThreshold: 3, recoveryTimeout: 30000 },
    healthCheck: { interval: 30000, timeout: 5000 },
    heartbeat: { interval: 10000, missLimit: 3 },
    payload: { maxBytes: 65536 },
    degradation: { level1QueueSize: 50, level2QueueSize: 80 },
    security: { maxChannelLength: 200, rateLimitPerMinute: 300 },
    connectionLogging: { interval: 60000 },
    connectionValidation: { interval: 30000 },
    gc: { sweepIntervalMs: 60_000 },
    serverSubscription: { enableDedupe: false, enableServerSubscription: false }
  } as const;

  private connectRetries = 0;
  private CONN_CHECK_INTERVAL: number = this.cfg.connCheckIntervalMs;

  private currentAccessToken: string | null = null;
  private lastConnectionVerificationAt: number | null = null;
  private readonly MAX_CONNECTION_VERIFICATION_FREQUENCY = this.cfg.connectionValidation.interval;

  // --- Batching & Health ---
  private isProcessingQueue = false;
  private readonly MAX_BATCH_SIZE = this.cfg.messageBatch.maxBatchSize;
  private lastHealthCheckAt = 0;
  private lastConnectionLoggingAt = 0;
  private healthStatus: Health = 'HEALTHY';
  private lastPongReceived = 0;
  private missedHeartbeats = 0;

  // --- Master timer ---
  private readonly MASTER_TIMER_INTERVAL = 1000;
  private lastTimerChecks = { connectionCheck: 0, healthCheck: 0, connectionLogging: 0, heartbeat: 0, batchProcessing: 0, gcSweep: 0 };
  private timerIntervals = {
    connectionCheck: this.cfg.connCheckIntervalMs,
    healthCheck: this.cfg.healthCheck.interval,
    connectionLogging: this.cfg.connectionLogging.interval,
    heartbeat: this.cfg.heartbeat.interval,
    batchProcessing: this.cfg.messageBatch.batchDelayMs,
    gcSweep: this.cfg.gc.sweepIntervalMs
  };

  // --- Analytics ---
  private firstConnectedAt: number | null = null;
  private sessionStartTime: number | null = null;
  private lastConnectionAt: number | null = null;
  private lastDisconnectionAt: number | null = null;
  private totalConnectionAttempts = 0;
  private totalReconnects = 0;
  private totalDisconnects = 0;
  private totalMessagesSent = 0;
  private totalMessagesReceived = 0;

  // --- Event bus & Subscriptions ---
  private readonly destroy$ = new Subject<void>();
  private onlineListener?: () => void;
  private offlineListener?: () => void;
  private boundVisibilityChange = this.handleVisibilityChange.bind(this);
  private boundFocus = this.handleFocus.bind(this);
  private boundUserClick = this.handleUserClick.bind(this);

  // --- Timers / timeouts ---
  private reconnectTimeoutId: any = null;
  private debounceTimeoutId: any = null;
  private rapidProbeTimer: any = null;
  private activeRetryTimeouts = new Map<string, RetryRecord>();

  // --- Misc flags ---
  private memoryPressureWarning = false;

  // --- Worker timer subscription ---
  private workerTimerSub: Subscription | null = null;

  // --- Web Lock name (unique per tab, set after browserId is resolved) ---
  private webLockName: string | null = null;

  constructor(
    private authService: AuthService,
    private store: Store<fromApp.AppState>,
    private loggingService: LoggingService,
    private fingerprintService: FingerprintService,
    private rafWatchdogService: RafWatchdogService,
    private payloadValidator: PayloadValidatorService,
    private circuitBreaker: CircuitBreakerService,
    private messageQueue: MessageQueueService,
    private workerTimer: SocketWorkerTimerService,
    private webLockService: WebLockService
  ) {
    this.configureServices();
    this.setupListeners();
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `Initial state: ${this.connectionState}`);
  }

  // --- Public API ---

  public async init() {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Initializing...');
    this.sessionStartTime = Date.now();
    this.browserId = await this.fingerprintService.getTabFingerprint();
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Browser Id:', this.browserId);
    this.initDone = true;

    this._transition({ type: 'INITIALIZE' });

    this.rafWatchdogService.start(this.cfg.frameTimeoutMs);
    this.startMasterTimer();
  }

  public verifyConnectionStatus(): void {
    this.verifyConnectionDebounced();
  }

  public getSocketIoObject() {
    return this.socket;
  }

  public callApiViaSocket(url: string, body: any = null) {
    this.verifyConnectionStatus();
    return this._sendAndListen(url, body);
  }

  /**
   * Low-level send + listen. Bypasses verifyConnectionStatus() so internal
   * callers (heartbeat, connection-verify) don't trigger circular checks.
   */
  private _sendAndListen(url: string, body: any = null) {
    const socketBodyPayload = { route: url, body };
    this.sendViaSocket({ action: 'api', data: socketBodyPayload });
    const channel = this.subscribeToChannel(url, true);
    return channel.data$.asObservable();
  }

  public subscribeToChannel(channelName: string, closeChannelAfterFirstResponse = false): ChannelStream {
    let info = this.activeChannelSubscriptions.get(channelName);
    const isNew = !info;

    if (!info) {
      info = { subject: new Subject<any>(), closeAfterFirstResponse: closeChannelAfterFirstResponse, isActive: true };
      this.activeChannelSubscriptions.set(channelName, info);
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Creating persistent subscription: ${channelName}`);
    } else {
      info.isActive = true;
      info.closeAfterFirstResponse = closeChannelAfterFirstResponse;
    }

    if (this.connectionState === 'CONNECTED' && isNew && !channelName.startsWith('_prefix_')) {
      this.sendChannelSubscription(channelName);
    }

    let channel = this.channels.get(channelName);
    if (!channel) {
      channel = { channelName, data$: info.subject, responseCount: 0, closeAfterFirstResponse: closeChannelAfterFirstResponse };
      this.channels.set(channelName, channel);
    }
    return channel;
  }

  public subscribeToChannelPrefix(prefix: string): ChannelStream {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Prefix subscribe "${prefix}"`);
    if (!this.activePrefixes.has(prefix)) {
      this.prefixTrie.insert(prefix);
      this.activePrefixes.add(prefix);
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Added new prefix subscription: ${prefix}`);
    } else {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Reusing existing prefix subscription: ${prefix}`);
    }
    const tempChannelName = `_prefix_${prefix}`;
    let info = this.activeChannelSubscriptions.get(tempChannelName);
    if (!info) {
      info = { subject: new Subject<any>(), closeAfterFirstResponse: false, isActive: true };
      this.activeChannelSubscriptions.set(tempChannelName, info);
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Created persistent prefix channel: ${tempChannelName}`);
    } else {
      info.isActive = true;
    }
    let existing = this.channels.get(tempChannelName);
    if (!existing) {
      existing = { channelName: tempChannelName, data$: info.subject, responseCount: 0, closeAfterFirstResponse: false };
      this.channels.set(tempChannelName, existing);
    }
    return existing;
  }

  public unsubscribeFromChannel(channelName: string): void {
    const channelInfo = this.activeChannelSubscriptions.get(channelName);
    if (channelInfo) {
      channelInfo.isActive = false;
      try { channelInfo.subject.complete(); } catch { }
      this.activeChannelSubscriptions.delete(channelName);
      this.channels.delete(channelName);

      if (this.cfg.serverSubscription.enableServerSubscription) {
        if (this.connectionState === 'CONNECTED' && !channelName.startsWith('_prefix_') && (!this.cfg.serverSubscription.enableDedupe || this.serverSubscribed.has(channelName))) {
          this.sendViaSocket({ action: 'unsubscribe', data: { channel: channelName } });
          if (this.cfg.serverSubscription.enableDedupe) {
            this.serverSubscribed.delete(channelName);
          }
        }
      } if (channelName.startsWith('_prefix_')) {
        const prefix = channelName.slice('_prefix_'.length);
        this.prefixTrie.remove(prefix);
        this.activePrefixes.delete(prefix);
      }
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Unsubscribed from channel: ${channelName}`);
    }
  }

  public unsubscribeFromChannelPrefix(prefix: string): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Unsubscribe prefix "${prefix}"`);
    const tempName = `_prefix_${prefix}`;
    this.unsubscribeFromChannel(tempName);
  }

  public localNotify(channelName: string = 'jobs-notify', jobId: string = '' + Date.now(), title: string = 'Success', status: JobStatusType = 'finished', progress = 100, actions: JobActions[] = []) {
    const data: SocketJobsNotificationDataType = {
      channel: channelName, receiverId: 0, senderId: 0,
      payload: { queueName: 'long-jobs', title, jobId, createdBy: '0', status, progressPercentage: progress, actions }
    };
    this.broadcastToChannel(data.channel, data);
  }

  public getChannelCount(): number { return this.channels.size; }
  public getActiveChannels(): string[] { return Array.from(this.channels.keys()); }

  public getSocketConnectionState(): SocketConnectionState {
    const state: SocketConnectionState = {
      readyState: this.socket ? this.socket.readyState : undefined,
      firstConnectedAt: this.firstConnectedAt,
      sessionStartTime: this.sessionStartTime,
      lastConnectionAt: this.lastConnectionAt,
      lastDisconnectionAt: this.lastDisconnectionAt,
      heartbeatStatus: {
        lastPong: this.lastPongReceived,
        missedCount: this.missedHeartbeats
      },
      totalConnectionAttempts: this.totalConnectionAttempts,
      connectRetries: this.connectRetries,
      totalReconnects: this.totalReconnects,
      totalDisconnects: this.totalDisconnects,
      totalMessagesSent: this.totalMessagesSent,
      totalMessagesReceived: this.totalMessagesReceived
    };

    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: getSocketConnectionState:', state);
    return state;
  }

  // --- State Machine Implementation ---

  private _transition(event: ConnectionEvent): void {
    const previousState = this.connectionState;
    let newState = previousState;

    switch (this.connectionState) {
      case 'IDLE':
        if (event.type === 'INITIALIZE') {
          newState = 'WAITING_FOR_AUTH';
        }
        break;

      case 'WAITING_FOR_AUTH':
        if (event.type === 'AUTH_STATUS_CHANGED' && event.isAuthenticated && event.token) {
          this.currentAccessToken = event.token;
          newState = 'CONNECTING';
        }
        break;

      case 'CONNECTING':
        if (event.type === 'CONNECTION_SUCCESSFUL') {
          newState = 'CONNECTED';
        } else if (event.type === 'CONNECTION_FAILED') {
          this.circuitBreaker.recordFailure();
          newState = 'RECONNECTING';
        } else if (event.type === 'LOGOUT') {
          newState = 'DISCONNECTED';
        }
        break;

      case 'CONNECTED':
        if (event.type === 'CONNECTION_LOST') {
          newState = 'RECONNECTING';
        } else if (event.type === 'LOGOUT') {
          newState = 'DISCONNECTED';
        } else if (event.type === 'AUTH_STATUS_CHANGED' && (!event.isAuthenticated || this.currentAccessToken !== event.token)) {
          this.currentAccessToken = event.token;
          newState = event.isAuthenticated ? 'RECONNECTING' : 'DISCONNECTED';
        }
        break;

      case 'RECONNECTING':
        if (event.type === 'RECONNECT_TIMER_ELAPSED') {
          newState = 'CONNECTING';
        } else if (event.type === 'LOGOUT') {
          newState = 'DISCONNECTED';
        } else if (event.type === 'MANUAL_CONNECT') {
          this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Manual connect triggered, attempting immediate reconnect.');
          this._clearReconnectTimer();
          newState = 'CONNECTING';
        }
        break;

      case 'DISCONNECTED':
        if (event.type === 'AUTH_STATUS_CHANGED' && event.isAuthenticated && event.token) {
          this.resetSessionAnalytics();
          this.currentAccessToken = event.token;
          newState = 'CONNECTING';
        }
        break;
    }

    if (newState !== previousState) {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `State transition: ${previousState} -> ${newState}`, { event });
      this.connectionState = newState;
      this._onStateChanged(previousState, newState, event);
    }
  }

  private _onStateChanged(previous: ConnectionState, current: ConnectionState, event: ConnectionEvent) {
    // --- Exit previous state ---
    if (previous === 'CONNECTED') {
      this.socketConnectionStatus$.next(false);
      this.lastDisconnectionAt = Date.now();
      this.totalDisconnects++;
      this.stopRapidProbe();
      this.releaseWebLock();
    }
    if (previous === 'RECONNECTING') {
      this._clearReconnectTimer();
    }

    // --- Enter current state ---
    switch (current) {
      case 'CONNECTING':
        this._performConnect(event.type === 'AUTH_STATUS_CHANGED' && previous === 'DISCONNECTED');
        break;
      case 'CONNECTED':
        this.socketConnectionStatus$.next(true);
        this.circuitBreaker.recordSuccess();
        this.connectRetries = 0;
        if (this.cfg.serverSubscription.enableDedupe) {
          this.serverSubscribed.clear(); // Reset server subscription deduplication
        }
        const now = Date.now();
        if (this.firstConnectedAt === null) this.firstConnectedAt = now;
        this.lastConnectionAt = now;
        this.lastPongReceived = now;
        this.missedHeartbeats = 0;
        this.onSocketReconnected();
        this.stopRapidProbe();
        this.acquireWebLock();
        break;
      case 'RECONNECTING':
        const withTokenRefresh = (event.type === 'CONNECTION_LOST' && (event.code === 4401 || event.code === 4001)) ||
          (event.type === 'AUTH_STATUS_CHANGED');
        this._scheduleReconnect(withTokenRefresh);
        this.startRapidProbe();
        break;
      case 'DISCONNECTED':
        this._performDisconnect();
        this.releaseWebLock();
        break;
    }
  }

  // --- Connection Actions (now private helpers for the state machine) ---

  private async _performConnect(isNewLogin: boolean): Promise<void> {
    if (!this.initDone || !this.browserId || !this.currentAccessToken) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Pre-conditions for connect not met.', { init: this.initDone, token: !!this.currentAccessToken });
      this._transition({ type: 'CONNECTION_FAILED' });
      return;
    }
    if (!this.circuitBreaker.canExecute()) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Circuit breaker is OPEN. Connection blocked.');
      this._transition({ type: 'CONNECTION_FAILED' });
      return;
    }
    if (!navigator.onLine) {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Browser offline; skipping connect');
      this._transition({ type: 'CONNECTION_FAILED' });
      return;
    }

    // Check if token is expired or about to expire (within 60 seconds)
    if (isTokenExpired(this.currentAccessToken, 60)) {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Token expired or expiring soon, refreshing before connect...');
      try {
        await this.doRefreshAccessToken();
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Token refreshed successfully, proceeding with connect');
      } catch (error) {
        this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Token refresh failed during connect, transitioning to DISCONNECTED', error);
        this._transition({ type: 'LOGOUT' });
        return;
      }
    }

    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Performing connection...');
    this.totalConnectionAttempts++;
    this.connectRetries++;
    if (isNewLogin) this.resetSessionAnalytics();

    // Close any lingering socket before creating a new one
    this._detachAndCloseSocket('Replacing connection');

    this.endPoint = `${env.socketOrigin}?Auth=${this.currentAccessToken}&deviceId=${this.browserId}`;
    const newSocket = new WebSocket(this.endPoint);
    this.socket = newSocket;

    // Stale socket guard: all handlers check that `this.socket` still points
    // to the socket that fired the event. If a new connection was created in
    // the meantime, events from the old socket are silently ignored.
    const isCurrentSocket = () => this.socket === newSocket;

    newSocket.onopen = () => {
      if (!isCurrentSocket()) return;
      this.sendViaSocket({ action: 'welcome', data: { message: 'Hello from ' + this.browserId } });
      this._transition({ type: 'CONNECTION_SUCCESSFUL' });
    };

    newSocket.onerror = (error) => {
      if (!isCurrentSocket()) return;
      // Detach handlers so the subsequent onclose from the same socket
      // doesn't fire a second transition.
      this._detachHandlers(newSocket);
      this._transition({ type: 'CONNECTION_FAILED', error });
    };

    newSocket.onclose = ({ wasClean, code, reason }) => {
      if (!isCurrentSocket()) return;
      this._detachHandlers(newSocket);
      // If we're still in CONNECTING, treat close as a connection failure.
      // Otherwise (CONNECTED), treat it as a connection loss.
      if (this.connectionState === 'CONNECTING') {
        this._transition({ type: 'CONNECTION_FAILED' });
      } else {
        this._transition({ type: 'CONNECTION_LOST', wasClean, code, reason });
      }
    };

    newSocket.onmessage = ({ data }) => {
      if (!isCurrentSocket()) return;
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Message:', data);
      this.totalMessagesReceived++;
      let parsed: any;
      try {
        parsed = JSON.parse(data);
      } catch (e) {
        this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Invalid JSON:', e);
        return;
      }
      if (parsed.channel && Array.isArray(parsed.channel)) {
        parsed.channel.forEach((chan: string) => this.broadcastToChannel(chan, parsed));
      } else if (parsed.channel) {
        this.broadcastToChannel(parsed.channel, parsed);
      } else {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: No channel in payload', parsed);
      }
    };
  }

  private _performDisconnect(): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Performing disconnect.');
    this._detachAndCloseSocket('Client initiated disconnect');
    this.channels.clear();
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Disconnected. Persistent subs preserved; wrappers cleared.');
  }

  /**
   * Detach all event handlers from a WebSocket instance.
   * Prevents stale sockets from firing events after a new socket is created,
   * and prevents the onerror + onclose double-transition problem.
   */
  private _detachHandlers(ws: WebSocket): void {
    ws.onopen = null;
    ws.onerror = null;
    ws.onclose = null;
    ws.onmessage = null;
  }

  /**
   * Detach handlers from the current socket, close it, and null the reference.
   * Safe to call even when this.socket is already null.
   */
  private _detachAndCloseSocket(reason: string): void {
    if (this.socket) {
      this._detachHandlers(this.socket);
      try { this.socket.close(1000, reason); } catch { /* ignored */ }
      this.socket = null;
    }
  }

  private _scheduleReconnect(withTokenRefresh: boolean): void {
    this.totalReconnects++;
    this._performDisconnect(); // Ensure previous socket is closed
    const delay = this._getReconnectDelay();
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Scheduling reconnect in ${delay}ms.`);

    this.reconnectTimeoutId = setTimeout(async () => {
      if (withTokenRefresh && isTokenExpired(this.currentAccessToken, 60)) {
        // Only refresh if the current token is actually expired or expiring.
        // currentAccessToken is always kept in sync with the store (via auth
        // subscription), so this correctly reflects the latest token — even
        // if another caller (e.g. the HTTP interceptor) already refreshed it.
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Token expired/expiring, refreshing before reconnect.');
        try {
          await this.doRefreshAccessToken();
          this._transition({ type: 'RECONNECT_TIMER_ELAPSED' });
        } catch (error) {
          this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Token refresh failed, transitioning to DISCONNECTED.', error);
          this._transition({ type: 'LOGOUT' }); // Treat as a full logout
        }
      } else {
        if (withTokenRefresh) {
          this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Token still valid, skipping refresh before reconnect.');
        }
        this._transition({ type: 'RECONNECT_TIMER_ELAPSED' });
      }
    }, delay);
  }

  private _clearReconnectTimer(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private _getReconnectDelay(): number {
    if (this.connectRetries === 0) return 0;      // Immediate first attempt
    if (this.connectRetries === 1) return 500;     // 500ms second attempt
    if (this.connectRetries === 2) return 1500;    // 1.5s
    if (this.connectRetries === 3) return 3000;    // 3s
    const delay = Math.min(5000 * Math.pow(1.5, this.connectRetries - 4), 30000);
    return delay + Math.random() * 1000; // Jitter capped at 1s
  }

  // --- Event & System Listeners ---

  private setupListeners(): void {
    // Auth changes
    this.store.select('auth').pipe(takeUntil(this.destroy$)).subscribe(async (authState: fromAuth.State) => {
      const isAuthenticated = authState.isAuthenticated;
      const token = authState.accessToken;
      const isRefreshing = authState.refreshTokenInProgress;

      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Auth subscription fired - isAuthenticated: ${isAuthenticated}, hasToken: ${!!token}, isRefreshing: ${isRefreshing}, currentState: ${this.connectionState}`);

      // Always keep the local token in sync with the store, regardless of
      // socket state. This prevents stale tokens from being used during
      // reconnection or connect attempts after a refresh.
      if (token && token !== this.currentAccessToken) {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Syncing currentAccessToken from store');
        this.currentAccessToken = token;
      }

      // Skip reacting to auth changes while a token refresh is in progress.
      // The session is still valid — we just need a new access token.
      if (isRefreshing) {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Token refresh in progress, ignoring auth state change');
        return;
      }

      if (isAuthenticated && this.connectionState === 'DISCONNECTED') {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Login detected - resetting session and connecting');
        this._transition({ type: 'AUTH_STATUS_CHANGED', isAuthenticated, token });
      } else if (!isAuthenticated && this.connectionState !== 'DISCONNECTED') {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Logout detected - disconnecting socket');
        this._transition({ type: 'LOGOUT' });
      } else if (this.connectionState === 'WAITING_FOR_AUTH' && isAuthenticated && token) {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: WAITING_FOR_AUTH condition met - transitioning to CONNECTING');
        this._transition({ type: 'AUTH_STATUS_CHANGED', isAuthenticated, token });
      }
      // Note: Token changes while CONNECTED/RECONNECTING/CONNECTING do NOT
      // trigger reconnection. The existing WebSocket connection is already
      // authenticated (AWS API Gateway validates only on $connect).
      // The updated currentAccessToken (synced above) will be used for
      // future connection/reconnection attempts.
    });

    // Check initial auth state immediately
    setTimeout(() => {
      firstValueFrom(this.store.select('auth')).then((authState: fromAuth.State) => {
        const isAuthenticated = authState.isAuthenticated;
        const token = authState.accessToken;

        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Initial auth check - isAuthenticated: ${isAuthenticated}, hasToken: ${!!token}`);

        if (this.connectionState === 'WAITING_FOR_AUTH' && isAuthenticated && token) {
          this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Initial auth check - transitioning from WAITING_FOR_AUTH to CONNECTING');
          this._transition({ type: 'AUTH_STATUS_CHANGED', isAuthenticated, token });
        }
      }).catch((error: any) => {
        this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Error checking initial auth state:', error);
      });
    }, 100); // Small delay to ensure store is ready

    // RAF Watchdog
    this.rafWatchdogService.freezeDetected$.pipe(takeUntil(this.destroy$)).subscribe(delta => {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, `[rAF Watchdog]: Frame delay ${delta} ms - verifying connection`);
      this.verifyConnectionStatus();
    });

    // Network listeners
    this.onlineListener = () => {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Network online');
      this.verifyConnectionStatus();
      this.startRapidProbe();
    };
    this.offlineListener = () => {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, 'Network offline');
      // The socket 'onclose' event will handle the transition to RECONNECTING
      if (this.socket) {
        this.socket.close(4000, 'Network offline');
      }
      this.startRapidProbe();
    };
    window.addEventListener('online', this.onlineListener);
    window.addEventListener('offline', this.offlineListener);

    // Visibility / focus / click
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    window.addEventListener('focus', this.boundFocus);
    window.addEventListener('click', this.boundUserClick, { passive: true });
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Visibility listeners set up');
  }

  // --- Remaining private methods (mostly unchanged, some simplified) ---

  private verifyConnectionDebounced = this.debounce(() => this._verifyConnectionStatus(), 1000);

  private _verifyConnectionStatus(): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Verifying connection (State: ${this.connectionState})`);
    if (this.connectionState === 'CONNECTED') {
      // Debounce to prevent spamming
      const now = Date.now();
      const elapsed = now - (this.lastConnectionVerificationAt ?? 0);
      if (elapsed < this.MAX_CONNECTION_VERIFICATION_FREQUENCY) return;
      this.lastConnectionVerificationAt = now;

      this._sendAndListen('check-connection-status', { timestamp: now }).pipe(
        take(1),
        timeout(this.cfg.healthCheck.timeout) // Auto-complete if no response within health-check timeout (5s)
      ).subscribe({
        next: (r: any) => {
          if (r.status === 599 || r.status === 401) {
            this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, `[SocketService]: Server reports invalid session (${r.status}), reconnecting.`);
            if (this.socket) this.socket.close(r.status === 401 ? 4401 : 4599, 'Server session invalid');
          }
        },
        error: (err: any) => {
          this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: verifyConnectionStatus error:', err);
          if (this.socket) this.socket.close(4000, 'Verification ping failed');
        }
      });
    } else if (this.connectionState === 'RECONNECTING' || this.connectionState === 'WAITING_FOR_AUTH') {
      this._transition({ type: 'MANUAL_CONNECT' });
    }
  }

  private doRefreshAccessToken(): Promise<any> {
    return firstValueFrom(this.authService.refreshIfNeeded().pipe(
      tap(() => {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: refreshIfNeeded OK');
      }),
      catchError(err => {
        this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: refreshIfNeeded ERROR', err);
        this.store.dispatch(AuthActions.logout({}));
        throw err;
      })
    ));
  }

  private onSocketReconnected(): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Socket reconnected, resubscribing to active channels...');
    this.prefixTrie.clear();
    this.activePrefixes.clear();
    this.activeChannelSubscriptions.forEach((_info, channelName) => {
      if (channelName.startsWith('_prefix_')) {
        const prefix = channelName.slice('_prefix_'.length);
        if (!this.activePrefixes.has(prefix)) {
          this.prefixTrie.insert(prefix);
          this.activePrefixes.add(prefix);
        }
      } else {
        this.sendChannelSubscription(channelName);
      }
    });

    // Flush any pending retry timers
    if (this.activeRetryTimeouts.size > 0) {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Flushing ${this.activeRetryTimeouts.size} messages from retry queue.`);
      const records = Array.from(this.activeRetryTimeouts.values());
      this.activeRetryTimeouts.forEach(rec => clearTimeout(rec.timeout));
      this.activeRetryTimeouts.clear();
      records.forEach(rec => this.sendMessage(rec.payload, rec.retriesLeft, rec.id));
    }
  }

  // Master timer, message sending, and other helpers remain largely the same,
  // but now operate within the context of the state machine.
  private startMasterTimer(): void {
    const now = Date.now();
    this.lastTimerChecks = {
      connectionCheck: now,
      healthCheck: now,
      connectionLogging: now,
      heartbeat: now,
      batchProcessing: now,
      gcSweep: now
    };

    // SocketWorkerTimerService handles the strategy internally:
    // - Uses a Web Worker (immune to background-tab throttling) if available.
    // - Falls back to main-thread setInterval automatically if not.
    // Either way, tick$ emits at the requested interval.
    this.workerTimer.start(this.MASTER_TIMER_INTERVAL);
    this.workerTimerSub = this.workerTimer.tick$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.processMasterTimer());

    this.loggingService.log(
      LogLevel.DEBUG,
      SocketService.COMPONENT_NAME,
      `[SocketService]: Master timer started (strategy=${this.workerTimer.usingWorker ? 'worker' : 'fallback'})`
    );
  }

  private stopMasterTimer(): void {
    this.workerTimer.stop();
    if (this.workerTimerSub) {
      this.workerTimerSub.unsubscribe();
      this.workerTimerSub = null;
    }
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Master timer stopped');
  }

  private processMasterTimer(): void {
    const now = Date.now();
    try {
      if (now - this.lastTimerChecks.connectionCheck >= this.CONN_CHECK_INTERVAL) {
        this.performConnectionCheck();
        this.lastTimerChecks.connectionCheck = now;
      }
      if (this.connectionState === 'CONNECTED') { // Only run these when connected
        if (now - this.lastTimerChecks.healthCheck >= this.timerIntervals.healthCheck) {
          this.performHealthCheck();
          this.lastTimerChecks.healthCheck = now;
        }
        if (now - this.lastTimerChecks.connectionLogging >= this.timerIntervals.connectionLogging) {
          this.performConnectionLogging();
          this.lastTimerChecks.connectionLogging = now;
        }
        if (now - this.lastTimerChecks.heartbeat >= this.timerIntervals.heartbeat) {
          this.performHeartbeat();
          this.lastTimerChecks.heartbeat = now;
        }
      }
      if (now - this.lastTimerChecks.batchProcessing >= this.timerIntervals.batchProcessing) {
        this.performBatchProcessing();
        this.lastTimerChecks.batchProcessing = now;
      }
      if (now - this.lastTimerChecks.gcSweep >= this.timerIntervals.gcSweep) {
        this.performChannelGc();
        this.lastTimerChecks.gcSweep = now;
      }
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Master timer error:', error);
    }
  }

  private performConnectionCheck(): void {
    try {
      this.connectRetries = 0; // Reset periodic retry counter
      this.verifyConnectionStatus();
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: performConnectionCheck error:', error);
    }
  }

  // --- Teardown ---

  ngOnDestroy(): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: ngOnDestroy - cleaning up');
    this.destroy$.next();
    this.destroy$.complete();

    this._clearReconnectTimer();
    if (this.debounceTimeoutId) clearTimeout(this.debounceTimeoutId);
    this.stopRapidProbe();

    this.activeRetryTimeouts.forEach(rec => clearTimeout(rec.timeout));
    this.activeRetryTimeouts.clear();

    this.activeChannelSubscriptions.forEach(info => {
      try { info.subject.complete(); } catch { }
    });
    this.activeChannelSubscriptions.clear();

    this.circuitBreaker.destroy();
    this.messageQueue.destroy();
    this.payloadValidator.destroy();

    this.stopMasterTimer();

    if (this.onlineListener) window.removeEventListener('online', this.onlineListener);
    if (this.offlineListener) window.removeEventListener('offline', this.offlineListener);
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    window.removeEventListener('focus', this.boundFocus);
    window.removeEventListener('click', this.boundUserClick);

    this.releaseWebLock();
    this.rafWatchdogService.stop();
    this._performDisconnect();

    this.channels.clear();
    this.prefixTrie.clear();
    this.activePrefixes.clear();
    this.serverSubscribed.clear();

    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Cleanup complete');
  }

  // --- Other methods (unchanged) ---
  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Tab hidden - socket may be affected');
    } else {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Tab visible - verifying connection');
      this.verifyConnectionStatus();
      this.startRapidProbe();
    }
  }
  private handleFocus(): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'Window focused - verifying connection');
    this.verifyConnectionStatus();
    this.startRapidProbe();
  }
  private handleUserClick(_event: Event): void {
    if (this.connectionState !== 'CONNECTED' && this.connectionState !== 'CONNECTING' && this.currentAccessToken) {
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, 'User click - immediate reconnect attempt');
      this._transition({ type: 'MANUAL_CONNECT' });
    }
  }

  private startRapidProbe(): void {
    if (this.rapidProbeTimer) return;
    this.rapidProbeTimer = setInterval(() => {
      if (navigator.onLine && (this.connectionState === 'RECONNECTING' || this.connectionState === 'WAITING_FOR_AUTH') && this.currentAccessToken) {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Rapid probe connect attempt');
        this._transition({ type: 'MANUAL_CONNECT' });
      }
    }, 3000);
  }

  private stopRapidProbe(): void {
    if (this.rapidProbeTimer) {
      clearInterval(this.rapidProbeTimer);
      this.rapidProbeTimer = null;
    }
  }
  private resetSessionAnalytics(): void {
    this.sessionStartTime = Date.now();
    this.firstConnectedAt = null;
    this.lastConnectionAt = null;
    this.lastDisconnectionAt = null;
    this.totalConnectionAttempts = 0;
    this.totalReconnects = 0;
    this.totalDisconnects = 0;
    this.totalMessagesSent = 0;
    this.totalMessagesReceived = 0;
    this.connectRetries = 0;
  }
  private async performHealthCheck(): Promise<void> {
    const now = Date.now();
    if (now - this.lastHealthCheckAt < this.cfg.healthCheck.interval) return;
    this.lastHealthCheckAt = now;

    try {
      this.checkMemoryPressure();
      const conn = !!this.socket && this.socket.readyState === WebSocket.OPEN;
      const queue = this.messageQueue.size() < this.cfg.messageBatch.maxQueueSize * 0.8;
      const circuit = this.circuitBreaker.getStatus().state !== 'OPEN';

      this.healthStatus = conn && queue && circuit && !this.memoryPressureWarning ? 'HEALTHY' : conn && circuit ? 'DEGRADED' : 'UNHEALTHY';
      if (this.healthStatus === 'UNHEALTHY') this.applyGracefulDegradation();

      this.loggingService.log(
        LogLevel.DEBUG,
        SocketService.COMPONENT_NAME,
        `[SocketService]: Health=${this.healthStatus}, Conn=${conn}, Queue=${this.messageQueue.size()}, Circuit=${this.circuitBreaker.getStatus().state}`
      );
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Health check failed:', error);
      this.healthStatus = 'UNHEALTHY';
    }
  }
  private performConnectionLogging(): void {
    try {
      const now = Date.now();
      if (now - this.lastConnectionLoggingAt < this.cfg.connectionLogging.interval) return;
      this.lastConnectionLoggingAt = now;
      if (this.socket && this.socket.readyState === WebSocket.OPEN && this.connectionState === 'CONNECTED') {
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Socket active');
      }
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Connection logging error:', error);
    }
  }
  private performHeartbeat(): void {
    try {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
      const now = Date.now();
      if (now - this.lastPongReceived > this.cfg.heartbeat.interval * this.cfg.heartbeat.missLimit) {
        this.missedHeartbeats++;
        this.loggingService.log(
          LogLevel.WARN,
          SocketService.COMPONENT_NAME,
          `[SocketService]: Missed heartbeat ${this.missedHeartbeats}/${this.cfg.heartbeat.missLimit}`
        );
        if (this.missedHeartbeats >= this.cfg.heartbeat.missLimit) {
          this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Heartbeat failed, reconnecting...');
          if (this.socket) this.socket.close(4000, 'Heartbeat failed');
          return;
        }
      }
      this._sendAndListen('ping', { timestamp: now }).pipe(
        take(1),
        timeout(this.cfg.heartbeat.interval) // Auto-complete if no pong within one heartbeat interval
      ).subscribe({
        next: (r: any) => {
          if (r.status === 599 || r.status === 401) {
            this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME,
              `[SocketService]: Heartbeat ping returned invalid session (${r.status}), reconnecting.`);
            if (this.socket) this.socket.close(r.status === 401 ? 4401 : 4599, 'Server session invalid');
            return;
          }
          this.lastPongReceived = Date.now();
          this.missedHeartbeats = 0;
        },
        error: (err: any) => {
          this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Heartbeat ping error:', err);
        }
      });
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Heartbeat error:', error);
    }
  }
  private performBatchProcessing(): void {
    try {
      if (!this.isProcessingQueue && !this.messageQueue.isEmpty()) this.processMessageBatch();
    } catch (error) {
      this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: Batch processing error:', error);
    }
  }
  private hasObservers(s: Subject<any>): boolean {
    const anySub = s as any;
    return Array.isArray(anySub?.observers) && anySub.observers.length > 0;
  }
  private performChannelGc(): void {
    const toDelete: string[] = [];

    this.activeChannelSubscriptions.forEach((info, name) => {
      const hasObservers = this.hasObservers(info.subject);
      const shouldCollect = (!hasObservers) || !info.isActive;

      if (shouldCollect) {
        try { info.subject.complete(); } catch { }
        toDelete.push(name);

        if (name.startsWith('_prefix_')) {
          const prefix = name.slice('_prefix_'.length);
          this.prefixTrie.remove(prefix);
          this.activePrefixes.delete(prefix);
        }

        if (this.channels.has(name)) this.channels.delete(name);
        if (this.cfg.serverSubscription.enableDedupe) {
          this.serverSubscribed.delete(name);
        }
      }
    });

    toDelete.forEach(n => this.activeChannelSubscriptions.delete(n));

    if (toDelete.length) {
      this.loggingService.log(
        LogLevel.DEBUG,
        SocketService.COMPONENT_NAME,
        `[SocketService]: GC swept ${toDelete.length} channel(s): ${toDelete.join(', ')}`
      );
    }
  }

  private sendChannelSubscription(channelName: string): void {
    if (channelName.startsWith('_prefix_')) return; // client-side only
    if (!this.cfg.serverSubscription.enableServerSubscription) return;
    if (this.cfg.serverSubscription.enableDedupe && this.serverSubscribed.has(channelName)) return; // already subscribed

    this.sendViaSocket({
      action: 'subscribe',
      data: { channel: channelName, reconnection: true }
    });
    if (this.cfg.serverSubscription.enableDedupe) {
      this.serverSubscribed.add(channelName);
    }
  }
  private debounce(func: Function, wait: number) {
    return (...args: any[]) => {
      if (this.debounceTimeoutId) clearTimeout(this.debounceTimeoutId);
      this.debounceTimeoutId = setTimeout(() => {
        func.apply(this, args);
        this.debounceTimeoutId = null;
      }, wait);
    };
  }
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
  private sendViaSocket(payload: any, retriesLeft = 5): void {
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Queueing payload:', payload);

    if (this.activeRetryTimeouts.size >= this.cfg.retry.maxRetryQueueSize) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME,
        `[SocketService]: Dropped message due to retry queue limit (${this.cfg.retry.maxRetryQueueSize})`);
      return;
    }

    const validation = this.payloadValidator.validatePayload(payload);
    if (!validation.isValid) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, `[SocketService]: Invalid payload: ${validation.reason}`, validation.details);
      return;
    }

    const rate = this.payloadValidator.checkRateLimit();
    if (!rate.allowed) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Dropped due to rate limit', rate.status);
      return;
    }

    const queueMessage: QueueMessage = {
      id: this.generateMessageId(),
      channel: payload.channel || 'default',
      data: payload,
      timestamp: Date.now(),
      retryCount: 5 - retriesLeft
    };

    if (!this.messageQueue.enqueue(queueMessage)) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Queue rejected message');
      return;
    }
  }
  private async processMessageBatch(): Promise<void> {
    if (this.isProcessingQueue || this.messageQueue.isEmpty()) return;
    this.isProcessingQueue = true;

    const batch = this.messageQueue.dequeue(this.MAX_BATCH_SIZE);

    if (batch.length === 1 && this.messageQueue.isEmpty()) {
      const item = batch[0];
      await this.sendMessage(item.data, item.retryCount || 0, item.id);
    } else {
      const concurrencyLimit = 3;
      for (let i = 0; i < batch.length; i += concurrencyLimit) {
        const chunk = batch.slice(i, i + concurrencyLimit);
        await Promise.all(chunk.map(item =>
          this.sendMessage(item.data, item.retryCount || 0, item.id)
        ));
      }
    }

    this.isProcessingQueue = false;
  }
  private scheduleRetry(id: string, payload: any, nextRetriesLeft: number): void {
    if (this.activeRetryTimeouts.has(id)) {
      const existingRecord = this.activeRetryTimeouts.get(id)!;
      clearTimeout(existingRecord.timeout);
      this.activeRetryTimeouts.delete(id);
      this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Cleared existing retry timeout for: ${id}`);
    }

    if (this.activeRetryTimeouts.size >= this.cfg.retry.maxRetryQueueSize) {
      const oldestId = this.activeRetryTimeouts.keys().next().value;
      if (oldestId) {
        const oldestRecord = this.activeRetryTimeouts.get(oldestId)!;
        clearTimeout(oldestRecord.timeout);
        this.activeRetryTimeouts.delete(oldestId);
        this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME,
          `[SocketService]: Dropped oldest retry (${oldestId}) due to queue limit (${this.cfg.retry.maxRetryQueueSize})`);
      }
    }

    const attemptNumber = this.cfg.retry.maxRetries - nextRetriesLeft;
    const base = this.cfg.retry.baseDelay * Math.pow(this.cfg.retry.backoffMultiplier, attemptNumber);
    const capped = Math.min(base, 10000);
    const jitter = (Math.random() - 0.5) * 2 * (capped * this.cfg.retry.jitterFactor);
    const delay = Math.max(100, capped + jitter);

    const timeout = setTimeout(() => {
      this.activeRetryTimeouts.delete(id);
      this.sendMessage(payload, nextRetriesLeft, id);
    }, delay);

    this.activeRetryTimeouts.set(id, { timeout, payload, retriesLeft: nextRetriesLeft, id });
    this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Scheduled retry for ${id} in ${delay}ms (attempt ${attemptNumber + 1})`);
  }
  private async sendMessage(payload: any, retriesLeft: number, id?: string): Promise<void> {
    return new Promise(resolve => {
      this.totalMessagesSent++;

      if (!this.circuitBreaker.canExecute()) {
        this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Circuit breaker blocking send');
        resolve();
        return;
      }

      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.send(JSON.stringify(payload));
          this.circuitBreaker.recordSuccess();

          if (id && this.activeRetryTimeouts.has(id)) {
            const existingRecord = this.activeRetryTimeouts.get(id)!;
            clearTimeout(existingRecord.timeout);
            this.activeRetryTimeouts.delete(id);
            this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Cleared retry timeout for successful message: ${id}`);
          }

          resolve();
        } catch (e) {
          this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: sendMessage error, will retry if possible', e);
          this.circuitBreaker.recordFailure();
          if (retriesLeft > 0 && id) {
            this.scheduleRetry(id, payload, retriesLeft - 1);
          }
          resolve();
        }
      } else if (retriesLeft > 0 && id) {
        this.scheduleRetry(id, payload, retriesLeft - 1);
        resolve();
      } else {
        this.loggingService.log(LogLevel.ERROR, SocketService.COMPONENT_NAME, '[SocketService]: sendMessage retries exhausted');
        this.circuitBreaker.recordFailure();

        if (id && this.activeRetryTimeouts.has(id)) {
          const existingRecord = this.activeRetryTimeouts.get(id)!;
          clearTimeout(existingRecord.timeout);
          this.activeRetryTimeouts.delete(id);
          this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, `[SocketService]: Cleared retry timeout for exhausted message: ${id}`);
        }

        resolve();
      }
    });
  }
  private broadcastToChannel(channelName: string, data: any): void {
    if (!channelName || typeof channelName !== 'string') {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Invalid broadcast channel:', channelName);
      return;
    }

    const info = this.activeChannelSubscriptions.get(channelName);
    if (info && info.isActive) {
      info.lastMessage = data;
      info.subject.next(data);
      const ch = this.channels.get(channelName);
      if (ch) ch.responseCount++;

      if (info.closeAfterFirstResponse) {
        this.unsubscribeFromChannel(channelName);
        return;
      }
    }

    if (this.activePrefixes.size) {
      const prefixes = this.prefixTrie.findMatchingPrefixes(channelName);
      prefixes.forEach(prefix => {
        const tempName = `_prefix_${prefix}`;
        const prefixInfo = this.activeChannelSubscriptions.get(tempName);
        if (prefixInfo && prefixInfo.isActive) {
          prefixInfo.subject.next(data);
          const pc = this.channels.get(tempName);
          if (pc) pc.responseCount++;
        }
      });
    }
  }
  private applyGracefulDegradation(): void {
    if (this.healthStatus === 'UNHEALTHY' || this.memoryPressureWarning) {
      this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Applying graceful degradation');
      if (this.messageQueue.size() > 20) {
        this.messageQueue.clear();
        this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, '[SocketService]: Message queue cleared due to stress');
      }
    }
  }
  private checkMemoryPressure(): void {
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      const usedPercent = (memInfo.usedJSHeapSize / memInfo.totalJSHeapSize) * 100;
      if (usedPercent > 80 && !this.memoryPressureWarning) {
        this.memoryPressureWarning = true;
        this.loggingService.log(LogLevel.WARN, SocketService.COMPONENT_NAME, `[SocketService]: High memory usage: ${usedPercent.toFixed(1)}%`);
      } else if (usedPercent < 60 && this.memoryPressureWarning) {
        this.memoryPressureWarning = false;
        this.loggingService.log(LogLevel.DEBUG, SocketService.COMPONENT_NAME, '[SocketService]: Memory pressure relieved');
      }
    }
  }
  // --- Web Lock Management ---
  // Acquires a Web Lock to prevent Chrome from freezing this tab while
  // a WebSocket connection is active. Chrome freezes background tabs
  // (including Web Workers) after ~5 minutes of inactivity. Holding a
  // Web Lock signals to the browser that this tab is doing important work.
  // Uses a unique lock name per tab (via browserId) so multiple tabs of
  // the same app can each hold their own lock independently.

  private acquireWebLock(): void {
    if (!this.webLockName) {
      this.webLockName = `socket-keepalive-${this.browserId || 'default'}`;
    }
    this.webLockService.acquire(this.webLockName);
  }

  private releaseWebLock(): void {
    if (this.webLockName) {
      this.webLockService.release(this.webLockName);
    }
  }

  private configureServices(): void {
    this.circuitBreaker.configure({
      failureThreshold: this.cfg.circuitBreaker.failureThreshold,
      recoveryTimeout: this.cfg.circuitBreaker.recoveryTimeout
    });

    this.messageQueue.configure({
      maxSize: this.cfg.messageBatch.maxQueueSize,
      ttl: this.cfg.messageBatch.ttlMs,
      policy: this.cfg.messageBatch.policy,
      batchSize: this.cfg.messageBatch.maxBatchSize
    });

    this.payloadValidator.configure({
      maxBytes: this.cfg.payload.maxBytes,
      maxChannelLength: this.cfg.security.maxChannelLength,
      rateLimitPerMinute: this.cfg.security.rateLimitPerMinute
    });
  }
}
